import {
  DISCOUNT_TOAST_PORTAL_ID,
  ErrorBoundary,
  Z_INDEXES,
  mediaLargeScreens,
  mediaMinL,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, memo, useMemo, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import {BasketHint} from '../Basket/BasketHint/BasketHint';
import {HeaderTopRowContainer} from './HeaderTopRowContainer';
import {MetaBar} from './MetaBar/MetaBar';
import {useSearchBarDisplayState} from './SearchBar/hooks/useSearchBarDisplayState';
import {TopCategoryNavigation} from './TopCategoryNavigation/TopCategoryNavigation';
import {DESKTOP_STICKY_HEIGHT} from './constants';

export interface HeaderProps {
  voucherToastsShowDirection?: 'up' | 'down' | 'left' | 'right';
}

export const Header: FC<HeaderProps> = memo(function Header() {
  const notificationBellRef = useRef<HTMLDivElement>(null);

  const {
    searchInputRef,
    isSearchInTabletMode,
    isSearchBarExpanded,
    expandSearchBar,
    collapseSearchBar,
    showStickyHeaderSearchIcon,
    searchWrapperRef,
    handleStickySearchIconClick,
  } = useSearchBarDisplayState();

  const basketHint = useMemo(() => {
    return (
      <ErrorBoundary renderContext="basket_hint" ErrorComponent={null}>
        <BasketHint notificationBellRef={notificationBellRef} />
      </ErrorBoundary>
    );
  }, []);

  return (
    <>
      <ErrorBoundary renderContext="header_meta_bar">
        <MetaBar />
      </ErrorBoundary>
      <ErrorBoundary renderContext="header_top_row">
        <HeaderTopRowContainer
          notificationBellRef={notificationBellRef}
          showSearchIcon={showStickyHeaderSearchIcon}
          onSearchIconClick={handleStickySearchIconClick}
        />
      </ErrorBoundary>
      <Sticky
        style={{zIndex: Z_INDEXES.DESKTOP_HEADER}}
        defaultOffsetTop={DESKTOP_STICKY_HEIGHT}
      >
        <ShopHintsContainer>
          {basketHint}
          <div id={DISCOUNT_TOAST_PORTAL_ID} />
        </ShopHintsContainer>
      </Sticky>
      <ErrorBoundary renderContext="header_navigation">
        <TopCategoryNavigation
          searchInputRef={searchInputRef}
          searchWrapperRef={searchWrapperRef}
          isSearchInTabletMode={isSearchInTabletMode}
          isSearchBarExpanded={isSearchBarExpanded}
          expandSearchBar={expandSearchBar}
          collapseSearchBar={collapseSearchBar}
        />
      </ErrorBoundary>
    </>
  );
});

const ShopHintsContainer = styled.div`
  position: absolute;
  width: 100%;
  max-width: 450px;
  top: 100%;
  right: 20px;

  @media ${mediaLargeScreens} {
    right: 60px;
  }

  /**
   * On very large screens (2.5k+), the header content is centered with a max-width of 1920px.
   * We need to position the hint relative to this centered content area, not the viewport edge.
   * Calculate: (viewport-width - max-content-width) / 2 + padding
   */
  @media ${mediaMinL} {
    right: max(60px, calc((100vw - 1920px) / 2 + 60px));
  }
`;
